import { toast } from "react-toastify";
import { resolveAssetUrl } from "../../services/apiConfig";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faShirt } from "@fortawesome/free-solid-svg-icons";
import styles from "../profile/Profile.module.css";
import { AuthContext } from "../../context/AuthContext";
import {
  getMyPosts,
  getOtherUserProfilePost,
} from "../../services/postService";
import { FollowModal } from "../../components/modal/FollowModal";
import { PostModal } from "../../components/modal/PostModal";
import ProfileEdit from "../../components/profileModal/ProfileEdit";
import Loader from "../../components/Loader/Loader";
import Button from "../../components/Button/Button";
import { getFollowers, getFollowing } from "../../services/followService";
import { createConversation } from "../../services/chatService";
import { createFollow, deleteFollow } from "../../services/followService";



function MyProfile({ username, myPost }) {
  const { user } = useContext(AuthContext);
  return (
    <div className={styles.profile}>
      <ProfileCard
        username={username}
        posts={myPost}
        bio={user?.bio || "Nenhuma biografia disponível"}
        profileUser={user}
        isOwnProfile={true}
      />
      <hr />
      <Post posts={myPost} />
    </div>
  );
}

function OtherUserProfile({
  profileUser,
  username,
  otherUserPost,
  isOtherUserFollowing,
  setIsOtherUserFollowing,
}) {
  return (
    <div className={styles.profile}>
      <ProfileCard
        username={username}
        posts={otherUserPost}
        bio={profileUser?.bio || "Este usuário não possui biografia."}
        profileUser={profileUser}
        isOwnProfile={false}
        isOtherUserFollowing={isOtherUserFollowing}
        setIsOtherUserFollowing={setIsOtherUserFollowing}
      />
      <hr />
      <Post posts={otherUserPost} />
    </div>
  );
}

function ProfileCard({
  profileUser,
  username,
  posts,
  bio,
  isOwnProfile,
  isOtherUserFollowing,
  setIsOtherUserFollowing,
}) {
  const { user } = useContext(AuthContext);
  const [follower, setFollower] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isFollower, setIsFollower] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isUpdatingFollow, setIsUpdatingFollow] = useState(false);
  const followRequestPending = useRef(false);
  const navigate = useNavigate();
  const chatRequestPending = useRef(false);
  const [openingChat, setOpeningChat] = useState(false);
  const openChat = async () => {
    if (!profileUser?.id || chatRequestPending.current) return;
    chatRequestPending.current = true; setOpeningChat(true);
    try {
      const conversation = await createConversation(profileUser.id);
      navigate("/messages", { state: { conversation: { ...conversation, other_user: profileUser } } });
    } catch (error) {
      toast.error(error.response?.data?.message || "Não foi possível abrir a conversa. Tente novamente.");
    } finally { chatRequestPending.current = false; setOpeningChat(false); }
  };

  const handleEdit = (value) => {
    setIsEdit(value);
  };

  const handleFollower = (value) => {
    setIsFollower(value);
  };

  const handleFollowing = (value) => {
    setIsFollowing(value);
  };


  const handleOtherUserFollow = async (otherUserId, value) => {
    if (followRequestPending.current) return;
    followRequestPending.current = true;
    setIsUpdatingFollow(true);
    try {
      const response = value
        ? await deleteFollow(user.id, otherUserId, username)
        : await createFollow(user.id, otherUserId, username);
      if (!response.success) throw new Error(response.message || "Não foi possível atualizar o vínculo.");
      setIsOtherUserFollowing(response.isFollowing ?? !value);
      toast.success(value ? "Você deixou de seguir este usuário." : "Usuário seguido!");
      try {
        const updated = await getFollowers(username);
        setFollower(updated.myFollowers);
      } catch (error) { console.error("Não foi possível atualizar a lista de seguidores:", error); }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Não foi possível atualizar o vínculo.");
    } finally {
      followRequestPending.current = false;
      setIsUpdatingFollow(false);
    }
  };

  useEffect(() => {
    const fetchFollower = async () => {
      try {
        const response = await getFollowers(username);
        setFollower(response.myFollowers);
      } catch (error) {
        console.error("Unable to fetch follower", error);
      }
    };
    fetchFollower();
  }, [username]);

  useEffect(() => {
    const fetchFollowing = async () => {
      try {
        const response = await getFollowing(username);
        setFollowing(response.myFollowings);
      } catch (error) {
        console.error("Unable to fetch follower", error);
      }
    };
    fetchFollowing();
  }, [username]);

  return (
    <div className={styles.profileInfo}>
      <div className={styles.profilePicture}>
        <div className={styles.profileImageContainer}>
          <img
            src={
              profileUser?.profile_url
                ? resolveAssetUrl(profileUser.profile_url)
                : resolveAssetUrl("images/profile_image/user.png")
            }
            alt=""
          />
        </div>
      </div>
      <div className={styles.profileDetails}>
        <div className={styles.nameAndOptions}>
          <h3>{username}</h3>
          {isOwnProfile ? (
            <>
              <Button size="sm" onClick={() => handleEdit(true)}>Editar perfil</Button>
              <Link className={styles.wardrobeLink} to="/wardrobe"><FontAwesomeIcon icon={faShirt} /> Meu Guarda-Roupa</Link>
              <ProfileEdit isVisible={isEdit} handleModal={handleEdit} />
            </>
          ) : (
            <>
              {" "}

              <Button type="button" variant={isOtherUserFollowing ? "secondary" : "primary"} disabled={isUpdatingFollow} onClick={() => handleOtherUserFollow(profileUser?.id, isOtherUserFollowing)}>
                {isUpdatingFollow ? "Atualizando..." : isOtherUserFollowing ? "Deixar de seguir" : "Seguir"}
              </Button>
              <Button type="button" variant="secondary" className={styles.chatButton} onClick={openChat} disabled={openingChat || !profileUser?.id} aria-label={`Conversar com ${username}`} title={openingChat ? "Abrindo conversa..." : "Enviar mensagem"}><FontAwesomeIcon icon={faCommentDots} aria-hidden="true" /></Button>
            </>
          )}
        </div>
        <div className={styles.postsAndFollows}>
          <div className={styles.postsCount}>{posts.length || 0} publicações</div>
          <div
            className={styles.followerCount}
            onClick={() => handleFollower(true)}
          >
            {follower.length || 0} seguidores
          </div>
          <div
            className={styles.followingCount}
            onClick={() => handleFollowing(true)}
          >
            {following.length || 0} seguindo
          </div>
          <FollowModal
            isVisible={isFollower}
            handleModal={handleFollower}
            users={follower}
            heading={"Seguidores"}
          />
          <FollowModal
            isVisible={isFollowing}
            handleModal={handleFollowing}
            users={following}
            heading={"Seguindo"}
          />
        </div>
        <div className={styles.bio}>{bio}</div>
      </div>
    </div>
  );
}

function Post({ posts }) {
  const [isPostModalVisible, setIsPostModalVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const handlePostModal = (value, post = null) => {
    setSelectedPost(post);
    setIsPostModalVisible(value);
  };

  return (
    <div className={styles.userPost}>
      {posts.length > 0 ? (
        posts.map((post) => (
          <div className={styles.imageContainer} key={post.id}>
            <img
              src={resolveAssetUrl(post.image_url)}
              alt={post.id}
              onClick={() => handlePostModal(true, post)}
            />
          </div>
        ))
      ) : (
        <p className={styles.noPost}>Nenhuma publicação para exibir</p>
      )}
      {selectedPost && (
        <PostModal
          isVisible={isPostModalVisible}
          handleModal={setIsPostModalVisible}
          post={selectedPost}
        />
      )}
    </div>
  );
}

function Profile({ refreshVersion = 0 }) {
  const { username } = useParams();
  const { user } = useContext(AuthContext);
  const [myPost, setMyPost] = useState([]);
  const [isOtherUserFollowing, setIsOtherUserFollowing] = useState();
  const [otherUserPost, setOtherUserPost] = useState([]);
  const [profileUser, setProfileUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        if (username === user.username) {
          const response = await getMyPosts();
          setMyPost(response.posts);
        } else {
          const response = await getOtherUserProfilePost(username);
          setOtherUserPost(response.posts);
          setProfileUser(response.profile);
          setIsOtherUserFollowing(response.isFollowingOtherUser ? true : false);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };
    fetchPosts();
  }, [username, user.username, refreshVersion]);

  return (
    user &&
    (isLoading ? (
      <Loader />
    ) : username === user.username ? (
      <MyProfile username={user.username} myPost={myPost} />
    ) : (
      <OtherUserProfile
        username={username}
        otherUserPost={otherUserPost}
        profileUser={profileUser}
        isOtherUserFollowing={isOtherUserFollowing}
        setIsOtherUserFollowing={setIsOtherUserFollowing}
      />
    ))
  );
}

export default Profile;
